import React, { useState, useEffect } from 'react'
import { Link } from 'react-router-dom'
import { publicApi } from '@/services/publicApi'
import { CommitteeMember } from '@/types'

// Utility function to scroll to top
const scrollToTop = () => {
  window.scrollTo({
    top: 0,
    left: 0,
    behavior: 'smooth'
  })
}

const getBaseUrl = () => {
  const envBase = import.meta.env.VITE_API_BASE_URL || import.meta.env.VITE_PUBLIC_URL || window.location.origin;
  return envBase.endsWith('/') ? envBase.slice(0, -1) : envBase;
};

const getProfileImageUrl = (url?: string) => {
  if (!url) return '';

  const cleaned = url.trim();
  if (!cleaned) return '';

  if (cleaned.startsWith('http://') || cleaned.startsWith('https://')) {
    try {
      const parsed = new URL(cleaned);
      const isProfileApiPath = parsed.pathname.startsWith('/api/v1/image-upload/public-file/profile/');
      const isUploadsPath = parsed.pathname.startsWith('/uploads/images/');

      // Keep profile images same-origin to avoid cross-origin policy blocks on some hosts.
      if ((isProfileApiPath || isUploadsPath) && parsed.origin !== window.location.origin) {
        return encodeURI(`${window.location.origin}${parsed.pathname}`);
      }
    } catch (error) {
      // Fall back to original URL if parsing fails.
    }
    return encodeURI(cleaned);
  }

  const baseUrl = getBaseUrl();
  if (cleaned.startsWith('/')) {
    return encodeURI(`${baseUrl}${cleaned}`);
  }

  return encodeURI(`${baseUrl}/${cleaned}`);
};

// Icon components for better styling
const CalendarIcon = ({ className = "w-5 h-5" }) => (
  <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
  </svg>
);

const BellIcon = ({ className = "w-5 h-5" }) => (
  <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 17h5l-5 5v-5zM4.828 7l2.586 2.586a2 2 0 002.828 0L16 7m-5 5a3 3 0 11-6 0 3 3 0 016 0z" />
  </svg>
);

const DocumentIcon = ({ className = "w-5 h-5" }) => (
  <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
  </svg>
);

const BuildingIcon = ({ className = "w-5 h-5" }) => (
  <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
  </svg>
);

const LocationIcon = ({ className = "w-4 h-4" }) => (
  <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
  </svg>
);

const PhoneIcon = ({ className = "w-4 h-4" }) => (
  <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
  </svg>
);

const ClockIcon = ({ className = "w-4 h-4" }) => (
  <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
  </svg>
);

const EmailIcon = ({ className = "w-4 h-4" }) => (
  <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 4.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
  </svg>
);

const ImageIcon = ({ className = "w-8 h-8" }) => (
  <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
  </svg>
);

// Tab components
const HomeTab = () => {
  const [photos, setPhotos] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [communityStats, setCommunityStats] = useState({
    total_residents: 0,
    complaint_resolution_rate: 0,
    support_available: '24/7',
    total_businesses: 0
  });
  const [statsLoading, setStatsLoading] = useState(true);

  useEffect(() => {
    const fetchPhotos = async () => {
      try {
        const response = await publicApi.getPhotos({ limit: 6, featured: true });
        setPhotos(response.data || []);
      } catch (error) {
        console.error('Error fetching photos:', error);
      } finally {
        setLoading(false);
      }
    };

    const fetchCommunityStats = async () => {
      try {
        const response = await publicApi.getCommunityStats();
        if (response.success && response.data) {
          setCommunityStats(response.data);
        }
      } catch (error) {
        console.error('Error fetching community stats:', error);
      } finally {
        setStatsLoading(false);
      }
    };

    fetchPhotos();
    fetchCommunityStats();
  }, []);

  return (
    <div className="space-y-8">
      {/* Hero Section */}
      <section className="relative bg-gradient-to-br from-blue-600 via-blue-700 to-blue-800 text-white rounded-lg overflow-hidden">
        <div className="absolute inset-0 bg-black opacity-20 rounded-lg"></div>
        <div className="relative px-4 sm:px-6 lg:px-8 py-8 sm:py-12 lg:py-16">
          <div className="text-center">
            <h1 className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-bold mb-3 sm:mb-4 leading-tight">
              Welcome to
              <span className="block text-blue-200 mt-1 sm:mt-2">New Friends Colony RWA</span>
            </h1>
            <p className="text-sm sm:text-base md:text-lg lg:text-xl text-blue-100 mb-4 sm:mb-6 max-w-2xl lg:max-w-3xl mx-auto px-2 sm:px-0">
              Your trusted partner in community management. We provide efficient services, 
              transparent communication, and a harmonious living environment for all residents.
            </p>
            <div className="flex flex-col sm:flex-row gap-3 sm:gap-4 justify-center items-center">
              <a
                href="https://wa.me/919810375098"
                target="_blank"
                rel="noopener noreferrer"
                className="w-full sm:w-auto bg-green-600 text-white px-6 sm:px-8 py-3 sm:py-3 rounded-lg font-medium hover:bg-green-700 transition-colors flex items-center justify-center space-x-2 text-sm sm:text-base"
              >
                <svg className="w-5 h-5 sm:w-6 sm:h-6" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893A11.821 11.821 0 0020.885 3.488"/>
                </svg>
                <span>Let's Talk</span>
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Photo Gallery Section */}
      <section className="py-8 sm:py-12">
        <div className="text-center mb-8 sm:mb-12">
          <h2 className="text-xl sm:text-2xl md:text-3xl font-bold text-gray-900 mb-3 sm:mb-4">
            Society Gallery
          </h2>
          <p className="text-sm sm:text-base md:text-lg text-gray-600 max-w-2xl lg:max-w-3xl mx-auto px-4 sm:px-0">
            Take a look at our beautiful society and recent activities
          </p>
        </div>

        {loading ? (
          <div className="flex justify-center py-8 sm:py-12">
            <div className="animate-spin rounded-full h-6 w-6 sm:h-8 sm:w-8 border-b-2 border-blue-600"></div>
          </div>
        ) : photos.length > 0 ? (
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-2 sm:gap-3 md:gap-4">
            {photos.map((photo) => (
              <div key={photo.id} className="relative group cursor-pointer">
                <div className="aspect-square rounded-lg overflow-hidden bg-gray-200">
                  <img
                    src={photo.thumbnail_url || photo.image_url}
                    alt={photo.title}
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                    loading="lazy"
                    onError={(e) => {
                      // Fallback to full image if thumbnail fails
                      if (e.currentTarget.src !== photo.image_url) {
                        e.currentTarget.src = photo.image_url;
                      }
                    }}
                  />
                </div>
                <div className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-30 transition-all duration-300 rounded-lg flex items-center justify-center">
                  <div className="opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                    <svg className="w-6 h-6 sm:w-8 sm:h-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0zM10 7v3m0 0v3m0-3h3m-3 0H7" />
                    </svg>
                  </div>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="text-center py-8 sm:py-12">
            <div className="w-12 h-12 sm:w-16 sm:h-16 mx-auto mb-3 sm:mb-4 bg-gray-100 rounded-full flex items-center justify-center">
              <ImageIcon className="w-6 h-6 sm:w-8 sm:h-8 text-gray-400" />
            </div>
            <h3 className="text-base sm:text-lg font-medium text-gray-900 mb-2">No photos available</h3>
            <p className="text-sm sm:text-base text-gray-500">Check back later for gallery updates.</p>
          </div>
        )}
      </section>

      {/* Quick Actions Section */}
      <section className="py-8 sm:py-12">
        <div className="text-center mb-8 sm:mb-12">
          <h2 className="text-xl sm:text-2xl md:text-3xl font-bold text-gray-900 mb-3 sm:mb-4">
            Quick Actions
          </h2>
          <p className="text-sm sm:text-base md:text-lg text-gray-600 max-w-2xl lg:max-w-3xl mx-auto px-4 sm:px-0">
            Get things done quickly with our streamlined services
          </p>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 sm:gap-6">
          <Link to="/complaint" className="group" onClick={scrollToTop}>
            <div className="bg-gradient-to-br from-red-500 to-red-600 text-white rounded-xl p-4 sm:p-6 shadow-lg hover:shadow-xl transition-all duration-300 transform hover:-translate-y-1">
              <div className="flex items-center justify-between mb-3 sm:mb-4">
                <div className="w-10 h-10 sm:w-12 sm:h-12 bg-white bg-opacity-20 rounded-lg flex items-center justify-center">
                  <svg className="w-5 h-5 sm:w-6 sm:h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z" />
                  </svg>
                </div>
                <svg className="w-4 h-4 sm:w-5 sm:h-5 group-hover:translate-x-1 transition-transform" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                </svg>
              </div>
              <h3 className="text-lg sm:text-xl font-semibold mb-2">Register Complaint</h3>
              <p className="text-red-100 text-xs sm:text-sm">Report issues and track resolution status</p>
            </div>
          </Link>

          <Link to="/enquiry" className="group" onClick={scrollToTop}>
            <div className="bg-gradient-to-br from-green-500 to-green-600 text-white rounded-xl p-4 sm:p-6 shadow-lg hover:shadow-xl transition-all duration-300 transform hover:-translate-y-1">
              <div className="flex items-center justify-between mb-3 sm:mb-4">
                <div className="w-10 h-10 sm:w-12 sm:h-12 bg-white bg-opacity-20 rounded-lg flex items-center justify-center">
                  <svg className="w-5 h-5 sm:w-6 sm:h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8.228 9c.549-1.165 2.03-2 3.772-2 2.21 0 4 1.343 4 3 0 1.4-1.278 2.575-3.006 2.907-.542.104-.994.54-.994 1.093m0 3h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                </div>
                <svg className="w-4 h-4 sm:w-5 sm:h-5 group-hover:translate-x-1 transition-transform" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                </svg>
              </div>
              <h3 className="text-lg sm:text-xl font-semibold mb-2">General Enquiry</h3>
              <p className="text-green-100 text-xs sm:text-sm">Ask questions and get quick answers</p>
            </div>
          </Link>
        </div>
      </section>

      {/* Community Stats */}
      <section className="py-8 sm:py-12 bg-white rounded-xl shadow-sm">
        <div className="text-center mb-6 sm:mb-8">
          <h2 className="text-xl sm:text-2xl md:text-3xl font-bold text-gray-900 mb-3 sm:mb-4">
            Community at a Glance
          </h2>
          <p className="text-sm sm:text-base md:text-lg text-gray-600">Our community by the numbers</p>
        </div>

        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-6 lg:gap-8">
          <div className="text-center">
            {statsLoading ? (
              <div className="animate-pulse">
                <div className="h-8 sm:h-10 bg-gray-200 rounded mb-1 sm:mb-2 mx-auto w-20 sm:w-24"></div>
                <div className="h-4 bg-gray-200 rounded w-16 mx-auto"></div>
              </div>
            ) : (
              <>
                <div className="text-2xl sm:text-3xl font-bold text-blue-600 mb-1 sm:mb-2">
                  {communityStats.total_residents > 0 ? `${communityStats.total_residents}+` : '0'}
                </div>
                <div className="text-xs sm:text-sm text-gray-600">Residents</div>
              </>
            )}
          </div>
          <div className="text-center">
            {statsLoading ? (
              <div className="animate-pulse">
                <div className="h-8 sm:h-10 bg-gray-200 rounded mb-1 sm:mb-2 mx-auto w-16 sm:w-20"></div>
                <div className="h-4 bg-gray-200 rounded w-24 mx-auto"></div>
              </div>
            ) : (
              <>
                <div className="text-2xl sm:text-3xl font-bold text-green-600 mb-1 sm:mb-2">
                  {communityStats.complaint_resolution_rate}%
                </div>
                <div className="text-xs sm:text-sm text-gray-600">Complaint Resolution</div>
              </>
            )}
          </div>
          <div className="text-center">
            {statsLoading ? (
              <div className="animate-pulse">
                <div className="h-8 sm:h-10 bg-gray-200 rounded mb-1 sm:mb-2 mx-auto w-12 sm:w-16"></div>
                <div className="h-4 bg-gray-200 rounded w-20 mx-auto"></div>
              </div>
            ) : (
              <>
                <div className="text-2xl sm:text-3xl font-bold text-purple-600 mb-1 sm:mb-2">
                  {communityStats.support_available}
                </div>
                <div className="text-xs sm:text-sm text-gray-600">Support Available</div>
              </>
            )}
          </div>
          <div className="text-center">
            {statsLoading ? (
              <div className="animate-pulse">
                <div className="h-8 sm:h-10 bg-gray-200 rounded mb-1 sm:mb-2 mx-auto w-16 sm:w-20"></div>
                <div className="h-4 bg-gray-200 rounded w-24 mx-auto"></div>
              </div>
            ) : (
              <>
                <div className="text-2xl sm:text-3xl font-bold text-orange-600 mb-1 sm:mb-2">
                  {communityStats.total_businesses > 0 ? `${communityStats.total_businesses}+` : '0'}
                </div>
                <div className="text-xs sm:text-sm text-gray-600">Local Businesses</div>
              </>
            )}
          </div>
        </div>
      </section>
    </div>
  );
};

const EventsTab = () => {
  const [events, setEvents] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedEvent, setSelectedEvent] = useState<any>(null);
  const [showDetailModal, setShowDetailModal] = useState(false);

  useEffect(() => {
    const fetchEvents = async () => {
      try {
        // Fetch all events (status='all' is passed by default in publicApi)
        const response = await publicApi.getEvents({ limit: 100, status: 'all' });
        console.log('Public events response:', response);
        if (response.success && response.data && Array.isArray(response.data)) {
          // Events are already sorted by backend (descending), but ensure they're sorted
          const sortedEvents = [...response.data].sort((a, b) => {
            return new Date(b.start_date).getTime() - new Date(a.start_date).getTime();
          });
          setEvents(sortedEvents);
        } else {
          console.error('Failed to fetch events:', response);
          setEvents([]);
        }
      } catch (error) {
        console.error('Error fetching events:', error);
        setEvents([]);
      } finally {
        setLoading(false);
      }
    };

    fetchEvents();
  }, []);

  const getTypeColor = (type: string) => {
    switch (type) {
      case 'meeting': return 'bg-blue-100 text-blue-800';
      case 'celebration': return 'bg-green-100 text-green-800';
      case 'maintenance': return 'bg-yellow-100 text-yellow-800';
      case 'announcement': return 'bg-purple-100 text-purple-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'upcoming': return 'bg-green-100 text-green-800';
      case 'ongoing': return 'bg-blue-100 text-blue-800';
      case 'completed': return 'bg-gray-100 text-gray-800';
      case 'cancelled': return 'bg-red-100 text-red-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  // Separate events into upcoming and past
  const now = new Date();
  const upcomingEvents = events.filter(event => {
    const eventDate = new Date(event.start_date);
    return eventDate >= now && event.status !== 'cancelled' && event.status !== 'completed';
  });

  const pastEvents = events.filter(event => {
    const eventDate = new Date(event.start_date);
    return eventDate < now || event.status === 'completed' || event.status === 'cancelled';
  });

  const handleViewDetails = (event: any) => {
    setSelectedEvent(event);
    setShowDetailModal(true);
  };

  const EventCard = ({ event }: { event: any }) => (
    <div 
      className="bg-white rounded-xl overflow-hidden shadow-sm border border-gray-200 hover:shadow-md transition-all cursor-pointer transform hover:-translate-y-1"
      onClick={() => handleViewDetails(event)}
    >
      {/* Event Image */}
      {event.attachments && event.attachments.images && event.attachments.images.length > 0 ? (
        <div className="w-full h-48 sm:h-56 overflow-hidden">
          <img
            src={event.attachments.images[0]}
            alt={event.title}
            className="w-full h-full object-cover"
            onError={(e) => {
              e.currentTarget.style.display = 'none';
            }}
          />
        </div>
      ) : (
        <div className="w-full h-48 sm:h-56 bg-gradient-to-br from-blue-100 to-purple-100 flex items-center justify-center">
          <CalendarIcon className="w-12 h-12 sm:w-16 sm:h-16 text-blue-400" />
        </div>
      )}
      <div className="p-4 sm:p-6">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between mb-3 sm:mb-4 gap-2 sm:gap-0">
          <div className="flex flex-wrap gap-2">
            <span className={`${getTypeColor(event.type)} text-xs sm:text-sm font-medium px-2 sm:px-3 py-1 rounded-full`}>
              {event.type}
            </span>
            {event.status && (
              <span className={`${getStatusColor(event.status)} text-xs sm:text-sm font-medium px-2 sm:px-3 py-1 rounded-full`}>
                {event.status}
              </span>
            )}
          </div>
          <span className="text-xs sm:text-sm text-gray-500 font-medium">
            {new Date(event.start_date).toLocaleDateString('en-US', {
              year: 'numeric',
              month: 'short',
              day: 'numeric'
            })}
          </span>
        </div>
        <h3 className="text-lg sm:text-xl font-semibold text-gray-900 mb-2 line-clamp-2">{event.title}</h3>
        {event.description && (
          <p className="text-sm sm:text-base text-gray-600 mb-3 sm:mb-4 line-clamp-3">{event.description}</p>
        )}
        <div className="space-y-2">
          {event.location && (
            <div className="flex items-center text-xs sm:text-sm text-gray-500">
              <LocationIcon className="w-3 h-3 sm:w-4 sm:h-4 mr-2 text-gray-400 flex-shrink-0" />
              <span className="truncate">{event.location}</span>
            </div>
          )}
          {event.start_date && (
            <div className="flex items-center text-xs sm:text-sm text-gray-500">
              <CalendarIcon className="w-3 h-3 sm:w-4 sm:h-4 mr-2 text-gray-400 flex-shrink-0" />
              <span>
                {new Date(event.start_date).toLocaleString('en-US', {
                  hour: '2-digit',
                  minute: '2-digit'
                })}
              </span>
            </div>
          )}
        </div>
        <button className="mt-4 text-sm text-blue-600 hover:text-blue-800 font-medium">
          View Details →
        </button>
      </div>
    </div>
  );

  if (loading) {
    return (
      <div className="space-y-4 sm:space-y-6">
        <div className="text-center mb-6 sm:mb-8">
          <h2 className="text-xl sm:text-2xl md:text-3xl font-bold text-gray-900 mb-3 sm:mb-4">Events</h2>
          <p className="text-sm sm:text-base md:text-lg text-gray-600 px-4 sm:px-0">Stay updated with all the latest events and activities in our society</p>
        </div>
        <div className="flex justify-center">
          <div className="animate-spin rounded-full h-6 w-6 sm:h-8 sm:w-8 border-b-2 border-blue-600"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-8 sm:space-y-12">
      {/* Upcoming Events Section */}
      <div>
      

        {upcomingEvents.length === 0 ? (
          <div className="text-center py-8 sm:py-12 bg-gray-50 rounded-lg">
            <div className="w-12 h-12 sm:w-16 sm:h-16 mx-auto mb-3 sm:mb-4 bg-gray-100 rounded-full flex items-center justify-center">
              <CalendarIcon className="w-6 h-6 sm:w-8 sm:h-8 text-gray-400" />
            </div>
            <h3 className="text-base sm:text-lg font-medium text-gray-900 mb-2">No upcoming events</h3>
            <p className="text-sm sm:text-base text-gray-500">Check back later for new events and activities.</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6">
            {upcomingEvents.map((event) => (
              <EventCard key={event.id} event={event} />
            ))}
          </div>
        )}
      </div>

      {/* Past Events Section */}
      {pastEvents.length > 0 && (
        <div>
          <div className="text-center mb-6 sm:mb-8">
            <h2 className="text-xl sm:text-2xl md:text-3xl font-bold text-gray-900 mb-3 sm:mb-4">Past Events</h2>
            <p className="text-sm sm:text-base md:text-lg text-gray-600 px-4 sm:px-0">Browse through our past events and activities</p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6">
            {pastEvents.map((event) => (
              <EventCard key={event.id} event={event} />
            ))}
          </div>
        </div>
      )}

      {/* Event Detail Modal */}
      {showDetailModal && selectedEvent && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4" onClick={() => setShowDetailModal(false)}>
          <div className="bg-white rounded-lg max-w-4xl w-full max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
            <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-4 flex items-center justify-between">
              <h2 className="text-2xl font-bold text-gray-900">Event Details</h2>
              <button
                onClick={() => setShowDetailModal(false)}
                className="text-gray-400 hover:text-gray-600"
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
            <div className="p-6">
              {/* Event Images */}
              {selectedEvent.attachments && selectedEvent.attachments.images && selectedEvent.attachments.images.length > 0 && (
                <div className="mb-6">
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    {selectedEvent.attachments.images.map((url: string, index: number) => (
                      <img
                        key={index}
                        src={url}
                        alt={`${selectedEvent.title} - Image ${index + 1}`}
                        className="w-full h-64 object-cover rounded-lg"
                      />
                    ))}
                  </div>
                </div>
              )}

              {/* Event Title and Type */}
              <div className="mb-6">
                <div className="flex flex-wrap items-center gap-3 mb-4">
                  <span className={`${getTypeColor(selectedEvent.type)} text-sm font-medium px-3 py-1 rounded-full`}>
                    {selectedEvent.type}
                  </span>
                  {selectedEvent.status && (
                    <span className={`${getStatusColor(selectedEvent.status)} text-sm font-medium px-3 py-1 rounded-full`}>
                      {selectedEvent.status}
                    </span>
                  )}
                </div>
                <h3 className="text-3xl font-bold text-gray-900 mb-4">{selectedEvent.title}</h3>
              </div>

              {/* Event Details Grid */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
                {/* Start Date */}
                <div className="bg-gray-50 p-4 rounded-lg">
                  <h4 className="text-sm font-medium text-gray-700 mb-2 flex items-center">
                    <CalendarIcon className="w-4 h-4 mr-2" />
                    Start Date & Time
                  </h4>
                  <p className="text-base text-gray-900 font-medium">
                    {new Date(selectedEvent.start_date).toLocaleString('en-US', {
                      weekday: 'long',
                      year: 'numeric',
                      month: 'long',
                      day: 'numeric',
                      hour: '2-digit',
                      minute: '2-digit'
                    })}
                  </p>
                </div>

                {/* End Date */}
                {selectedEvent.end_date && (
                  <div className="bg-gray-50 p-4 rounded-lg">
                    <h4 className="text-sm font-medium text-gray-700 mb-2 flex items-center">
                      <CalendarIcon className="w-4 h-4 mr-2" />
                      End Date & Time
                    </h4>
                    <p className="text-base text-gray-900 font-medium">
                      {new Date(selectedEvent.end_date).toLocaleString('en-US', {
                        weekday: 'long',
                        year: 'numeric',
                        month: 'long',
                        day: 'numeric',
                        hour: '2-digit',
                        minute: '2-digit'
                      })}
                    </p>
                  </div>
                )}

                {/* Location */}
                {selectedEvent.location && (
                  <div className="bg-gray-50 p-4 rounded-lg">
                    <h4 className="text-sm font-medium text-gray-700 mb-2 flex items-center">
                      <LocationIcon className="w-4 h-4 mr-2" />
                      Location
                    </h4>
                    <p className="text-base text-gray-900 font-medium">{selectedEvent.location}</p>
                  </div>
                )}

                {/* RSVP Info */}
                {selectedEvent.requires_rsvp && (
                  <div className="bg-blue-50 p-4 rounded-lg border border-blue-200">
                    <h4 className="text-sm font-medium text-blue-900 mb-1">RSVP Required</h4>
                    <p className="text-sm text-blue-700">
                      {selectedEvent.max_participants ? `Maximum ${selectedEvent.max_participants} participants` : 'RSVP is required for this event'}
                    </p>
                  </div>
                )}
              </div>

              {/* Description */}
              {selectedEvent.description && (
                <div className="mb-6">
                  <h4 className="text-lg font-semibold text-gray-900 mb-3">Description</h4>
                  <p className="text-base text-gray-700 whitespace-pre-wrap">{selectedEvent.description}</p>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

const NotificationsTab = () => {
  const [notifications, setNotifications] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchNotifications = async () => {
      try {
        const response = await publicApi.getNotifications({ limit: 10 });
        setNotifications(response.data || []);
      } catch (error) {
        console.error('Error fetching notifications:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchNotifications();
  }, []);

  const getTypeColor = (type: string) => {
    switch (type) {
      case 'urgent': return 'bg-red-100 text-red-800';
      case 'general': return 'bg-blue-100 text-blue-800';
      case 'update': return 'bg-green-100 text-green-800';
      case 'maintenance': return 'bg-yellow-100 text-yellow-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  if (loading) {
    return (
      <div className="space-y-4 sm:space-y-6">
        <div className="text-center mb-6 sm:mb-8">
          <h2 className="text-xl sm:text-2xl md:text-3xl font-bold text-gray-900 mb-3 sm:mb-4">Important Notifications</h2>
          <p className="text-sm sm:text-base md:text-lg text-gray-600 px-4 sm:px-0">Stay informed with the latest announcements and updates</p>
        </div>
        <div className="flex justify-center">
          <div className="animate-spin rounded-full h-6 w-6 sm:h-8 sm:w-8 border-b-2 border-blue-600"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-4 sm:space-y-6">
      <div className="text-center mb-6 sm:mb-8">
        <h2 className="text-xl sm:text-2xl md:text-3xl font-bold text-gray-900 mb-3 sm:mb-4">Important Notifications</h2>
        <p className="text-sm sm:text-base md:text-lg text-gray-600 px-4 sm:px-0">Stay informed with the latest announcements and updates</p>
      </div>

      {notifications.length === 0 ? (
        <div className="text-center py-8 sm:py-12">
          <div className="w-12 h-12 sm:w-16 sm:h-16 mx-auto mb-3 sm:mb-4 bg-gray-100 rounded-full flex items-center justify-center">
            <BellIcon className="w-6 h-6 sm:w-8 sm:h-8 text-gray-400" />
          </div>
          <h3 className="text-base sm:text-lg font-medium text-gray-900 mb-2">No notifications</h3>
          <p className="text-sm sm:text-base text-gray-500">Check back later for new announcements and updates.</p>
        </div>
      ) : (
        <div className="space-y-3 sm:space-y-4">
          {notifications.map((notification) => (
            <div key={notification.id} className="bg-white rounded-lg p-4 sm:p-6 shadow-sm border border-gray-200">
              <div className="flex items-start justify-between">
                <div className="flex-1">
                  <div className="flex flex-col sm:flex-row sm:items-center mb-2 sm:mb-2 gap-2 sm:gap-0">
                    <div className="flex items-center">
                      {notification.is_pinned && (
                        <svg className="w-3 h-3 sm:w-4 sm:h-4 text-yellow-500 mr-2 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                          <path fillRule="evenodd" d="M8 4a4 4 0 100 8 4 4 0 000-8zM2 8a6 6 0 1110.89 3.476l4.817 4.817a1 1 0 01-1.414 1.414l-4.816-4.816A6 6 0 012 8z" clipRule="evenodd" />
                        </svg>
                      )}
                      <span className={`${getTypeColor(notification.type)} text-xs font-medium px-2 py-1 rounded-full`}>
                        {notification.type}
                      </span>
                    </div>
                    <span className="text-xs sm:text-sm text-gray-500">
                      {new Date(notification.publish_date).toLocaleDateString()}
                    </span>
                  </div>
                  <h3 className="text-base sm:text-lg font-semibold text-gray-900 mb-2">{notification.title}</h3>
                  <p className="text-sm sm:text-base text-gray-600">{notification.content}</p>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

const PressReleaseTab = () => {
  const [pressReleases, setPressReleases] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedRelease, setSelectedRelease] = useState<any>(null);
  const [showDetailModal, setShowDetailModal] = useState(false);

  useEffect(() => {
    const fetchPressReleases = async () => {
      try {
        const response = await publicApi.getPressReleases({ limit: 100 });
        if (response.success && response.data) {
          // Sort by publish_date descending (latest first)
          const sorted = [...response.data].sort((a, b) => {
            return new Date(b.publish_date || b.created_at).getTime() - new Date(a.publish_date || a.created_at).getTime();
          });
          setPressReleases(sorted);
        } else {
          setPressReleases([]);
        }
      } catch (error) {
        console.error('Error fetching press releases:', error);
        setPressReleases([]);
      } finally {
        setLoading(false);
      }
    };

    fetchPressReleases();
  }, []);

  const getCategoryColor = (category: string) => {
    switch (category) {
      case 'official': return 'bg-blue-100 text-blue-800';
      case 'achievement': return 'bg-green-100 text-green-800';
      case 'announcement': return 'bg-yellow-100 text-yellow-800';
      case 'update': return 'bg-purple-100 text-purple-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const handleViewDetails = (release: any) => {
    setSelectedRelease(release);
    setShowDetailModal(true);
  };

  if (loading) {
    return (
      <div className="space-y-4 sm:space-y-6">
        <div className="text-center mb-6 sm:mb-8">
          <h2 className="text-xl sm:text-2xl md:text-3xl font-bold text-gray-900 mb-3 sm:mb-4">Press Releases</h2>
          <p className="text-sm sm:text-base md:text-lg text-gray-600 px-4 sm:px-0">Official announcements and press releases from the society management</p>
        </div>
        <div className="flex justify-center">
          <div className="animate-spin rounded-full h-6 w-6 sm:h-8 sm:w-8 border-b-2 border-blue-600"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-4 sm:space-y-6">
      <div className="text-center mb-6 sm:mb-8">
        <h2 className="text-xl sm:text-2xl md:text-3xl font-bold text-gray-900 mb-3 sm:mb-4">Press Releases</h2>
        <p className="text-sm sm:text-base md:text-lg text-gray-600 px-4 sm:px-0">Official announcements and press releases from the society management</p>
      </div>

      {pressReleases.length === 0 ? (
        <div className="text-center py-8 sm:py-12">
          <div className="w-12 h-12 sm:w-16 sm:h-16 mx-auto mb-3 sm:mb-4 bg-gray-100 rounded-full flex items-center justify-center">
            <DocumentIcon className="w-6 h-6 sm:w-8 sm:h-8 text-gray-400" />
          </div>
          <h3 className="text-base sm:text-lg font-medium text-gray-900 mb-2">No press releases</h3>
          <p className="text-sm sm:text-base text-gray-500">Check back later for new official announcements.</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6">
          {pressReleases.map((release) => (
            <div 
              key={release.id} 
              className="bg-white rounded-xl overflow-hidden shadow-sm border border-gray-200 hover:shadow-md transition-all cursor-pointer transform hover:-translate-y-1"
              onClick={() => handleViewDetails(release)}
            >
              {/* Featured Image */}
              {release.featured_image_url ? (
                <div className="w-full h-48 overflow-hidden">
                  <img
                    src={release.featured_image_url}
                    alt={release.title}
                    className="w-full h-full object-cover"
                  />
                </div>
              ) : (
                <div className="w-full h-48 bg-gradient-to-br from-red-100 to-orange-100 flex items-center justify-center">
                  <DocumentIcon className="w-16 h-16 text-red-400" />
                </div>
              )}
              
              <div className="p-4 sm:p-6">
                <div className="flex items-center justify-between mb-3">
                  <span className={`${getCategoryColor(release.category || 'general')} text-xs sm:text-sm font-medium px-2 sm:px-3 py-1 rounded-full`}>
                    {release.category || 'general'}
                  </span>
                  {release.is_featured && (
                    <span className="text-xs sm:text-sm font-medium px-2 sm:px-3 py-1 bg-yellow-100 text-yellow-800 rounded-full">
                      Featured
                    </span>
                  )}
                </div>
                
                <h3 className="text-lg sm:text-xl font-semibold text-gray-900 mb-2 line-clamp-2">{release.title}</h3>
                
                <p className="text-sm sm:text-base text-gray-600 mb-4 line-clamp-3">
                  {release.excerpt || release.content?.substring(0, 150) + '...'}
                </p>
                
                <div className="flex items-center justify-between">
                  <span className="text-xs sm:text-sm text-gray-500">
                    {new Date(release.publish_date || release.created_at).toLocaleDateString('en-US', {
                      year: 'numeric',
                      month: 'short',
                      day: 'numeric'
                    })}
                  </span>
                  <button className="text-sm text-blue-600 hover:text-blue-800 font-medium">
                    Read More →
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Detail Modal */}
      {showDetailModal && selectedRelease && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4" onClick={() => setShowDetailModal(false)}>
          <div className="bg-white rounded-lg max-w-4xl w-full max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
            <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-4 flex items-center justify-between">
              <h2 className="text-2xl font-bold text-gray-900">Press Release</h2>
              <button
                onClick={() => setShowDetailModal(false)}
                className="text-gray-400 hover:text-gray-600"
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
            <div className="p-6">
              {/* Featured Image */}
              {selectedRelease.featured_image_url && (
                <div className="mb-6">
                  <img
                    src={selectedRelease.featured_image_url}
                    alt={selectedRelease.title}
                    className="w-full h-64 object-cover rounded-lg"
                  />
                </div>
              )}

              {/* Category and Date */}
              <div className="flex items-center justify-between mb-4">
                <span className={`${getCategoryColor(selectedRelease.category || 'general')} text-sm font-medium px-3 py-1 rounded-full`}>
                  {selectedRelease.category || 'general'}
                </span>
                <span className="text-sm text-gray-500">
                  {new Date(selectedRelease.publish_date || selectedRelease.created_at).toLocaleDateString('en-US', {
                    weekday: 'long',
                    year: 'numeric',
                    month: 'long',
                    day: 'numeric'
                  })}
                </span>
              </div>

              {/* Title */}
              <h3 className="text-3xl font-bold text-gray-900 mb-4">{selectedRelease.title}</h3>

              {/* Content */}
              <div className="prose prose-lg max-w-none mb-6">
                <p className="text-base text-gray-700 whitespace-pre-wrap">{selectedRelease.content}</p>
              </div>

              {/* Attachments - Documents/PDFs */}
              {selectedRelease.attachments && (
                <div className="mb-6">
                  {(() => {
                    let attachments: string[] = [];
                    if (typeof selectedRelease.attachments === 'string') {
                      try {
                        attachments = JSON.parse(selectedRelease.attachments);
                      } catch (e) {
                        attachments = [];
                      }
                    } else if (Array.isArray(selectedRelease.attachments)) {
                      attachments = selectedRelease.attachments;
                    } else if (selectedRelease.attachments.images) {
                      attachments = selectedRelease.attachments.images;
                    }
                    
                    if (attachments.length > 0) {
                      return (
                        <div>
                          <h4 className="text-lg font-semibold text-gray-900 mb-3">Documents & Attachments</h4>
                          <div className="space-y-2">
                            {attachments.map((url: string, index: number) => {
                              const isImage = /\.(jpg|jpeg|png|gif|webp)$/i.test(url);
                              const fileName = url.split('/').pop() || `Attachment ${index + 1}`;
                              
                              return (
                                <div key={index} className="flex items-center p-3 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors">
                                  {isImage ? (
                                    <img
                                      src={url}
                                      alt={`${selectedRelease.title} - Image ${index + 1}`}
                                      className="w-16 h-16 object-cover rounded mr-3"
                                    />
                                  ) : (
                                    <div className="w-16 h-16 bg-blue-100 rounded flex items-center justify-center mr-3">
                                      <DocumentIcon className="w-8 h-8 text-blue-600" />
                                    </div>
                                  )}
                                  <div className="flex-1 min-w-0">
                                    <a
                                      href={url}
                                      target="_blank"
                                      rel="noopener noreferrer"
                                      className="text-sm font-medium text-blue-600 hover:text-blue-800 truncate block"
                                      onClick={(e) => e.stopPropagation()}
                                    >
                                      {fileName}
                                    </a>
                                    <p className="text-xs text-gray-500 mt-1">
                                      {isImage ? 'Image' : 'Document'} - Click to open
                                    </p>
                                  </div>
                                </div>
                              );
                            })}
                          </div>
                        </div>
                      );
                    }
                    return null;
                  })()}
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

const BusinessTab = () => {
  const [businesses, setBusinesses] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedBusiness, setSelectedBusiness] = useState<any>(null);
  const [showDetailModal, setShowDetailModal] = useState(false);

  useEffect(() => {
    const fetchBusinesses = async () => {
      try {
        const response = await publicApi.getBusinessListings({ limit: 100 });
        if (response.success && response.data) {
          // Sort by featured first, then by rating/created_at
          const sorted = [...response.data].sort((a, b) => {
            if (a.is_featured && !b.is_featured) return -1;
            if (!a.is_featured && b.is_featured) return 1;
            if (a.rating !== b.rating) return (b.rating || 0) - (a.rating || 0);
            return new Date(b.created_at).getTime() - new Date(a.created_at).getTime();
          });
          setBusinesses(sorted);
        } else {
          setBusinesses([]);
        }
      } catch (error) {
        console.error('Error fetching businesses:', error);
        setBusinesses([]);
      } finally {
        setLoading(false);
      }
    };

    fetchBusinesses();
  }, []);

  const renderStars = (rating: number) => {
    const stars = [];
    const fullStars = Math.floor(rating);
    const hasHalfStar = rating % 1 !== 0;

    for (let i = 0; i < fullStars; i++) {
      stars.push(<span key={i} className="text-yellow-400">★</span>);
    }
    if (hasHalfStar) {
      stars.push(<span key="half" className="text-yellow-400">☆</span>);
    }
    const remainingStars = 5 - Math.ceil(rating);
    for (let i = 0; i < remainingStars; i++) {
      stars.push(<span key={`empty-${i}`} className="text-gray-300">★</span>);
    }
    return stars;
  };

  const handleViewDetails = (business: any) => {
    setSelectedBusiness(business);
    setShowDetailModal(true);
  };

  if (loading) {
    return (
      <div className="space-y-4 sm:space-y-6">
        <div className="text-center mb-6 sm:mb-8">
          <h2 className="text-xl sm:text-2xl md:text-3xl font-bold text-gray-900 mb-3 sm:mb-4">Business Directory</h2>
          <p className="text-sm sm:text-base md:text-lg text-gray-600 px-4 sm:px-0">Discover local businesses and services within our community</p>
        </div>
        <div className="flex justify-center">
          <div className="animate-spin rounded-full h-6 w-6 sm:h-8 sm:w-8 border-b-2 border-blue-600"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-4 sm:space-y-6">
      <div className="text-center mb-6 sm:mb-8">
        <h2 className="text-xl sm:text-2xl md:text-3xl font-bold text-gray-900 mb-3 sm:mb-4">Business Directory</h2>
        <p className="text-sm sm:text-base md:text-lg text-gray-600 px-4 sm:px-0">Discover local businesses and services within our community</p>
      </div>

      {businesses.length === 0 ? (
        <div className="text-center py-8 sm:py-12">
          <div className="w-12 h-12 sm:w-16 sm:h-16 mx-auto mb-3 sm:mb-4 bg-gray-100 rounded-full flex items-center justify-center">
            <BuildingIcon className="w-6 h-6 sm:w-8 sm:h-8 text-gray-400" />
          </div>
          <h3 className="text-base sm:text-lg font-medium text-gray-900 mb-2">No business listings</h3>
          <p className="text-sm sm:text-base text-gray-500">Check back later for local business services.</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6">
          {businesses.map((business) => (
            <div 
              key={business.id} 
              className="bg-white rounded-xl overflow-hidden shadow-sm border border-gray-200 hover:shadow-md transition-all cursor-pointer transform hover:-translate-y-1"
              onClick={() => handleViewDetails(business)}
            >
              {/* Business Image */}
              {business.attachments && business.attachments.images && business.attachments.images.length > 0 ? (
                <div className="w-full h-48 overflow-hidden">
                  <img
                    src={business.attachments.images[0]}
                    alt={business.business_name}
                    className="w-full h-full object-cover"
                  />
                </div>
              ) : (
                <div className="w-full h-48 bg-gradient-to-br from-indigo-100 to-purple-100 flex items-center justify-center">
                  <BuildingIcon className="w-16 h-16 text-indigo-400" />
                </div>
              )}

              <div className="p-4 sm:p-6">
                <div className="flex flex-col sm:flex-row sm:items-center justify-between mb-3 sm:mb-4 gap-2 sm:gap-0">
                  <div className="flex flex-wrap gap-2">
                    <span className={`${business.is_verified ? 'bg-blue-100 text-blue-800' : 'bg-gray-100 text-gray-800'} text-xs sm:text-sm font-medium px-2 sm:px-3 py-1 rounded-full`}>
                      {business.is_featured ? 'Featured' : business.is_verified ? 'Verified' : 'New'}
                    </span>
                    <span className="text-xs sm:text-sm font-medium px-2 sm:px-3 py-1 bg-purple-100 text-purple-800 rounded-full">
                      {business.category}
                    </span>
                  </div>
                  {business.rating !== undefined && business.rating > 0 && (
                    <div className="flex items-center">
                      <div className="flex">
                        {renderStars(business.rating)}
                      </div>
                      <span className="text-xs sm:text-sm text-gray-500 ml-1">({business.rating.toFixed(1)})</span>
                    </div>
                  )}
                </div>
                
                <h3 className="text-lg sm:text-xl font-semibold text-gray-900 mb-2 line-clamp-2">{business.business_name}</h3>
                
                {business.description && (
                  <p className="text-sm sm:text-base text-gray-600 mb-3 sm:mb-4 line-clamp-2">{business.description}</p>
                )}
                
                <div className="space-y-2 mb-4">
                  {business.contact_phone && (
                    <div className="flex items-center text-xs sm:text-sm text-gray-500">
                      <PhoneIcon className="w-3 h-3 sm:w-4 sm:h-4 mr-2 text-gray-400 flex-shrink-0" />
                      <span className="truncate">{business.contact_phone}</span>
                    </div>
                  )}
                  {business.address && (
                    <div className="flex items-center text-xs sm:text-sm text-gray-500">
                      <LocationIcon className="w-3 h-3 sm:w-4 sm:h-4 mr-2 text-gray-400 flex-shrink-0" />
                      <span className="truncate">{business.address}</span>
                    </div>
                  )}
                </div>
                
                <button className="w-full text-sm text-blue-600 hover:text-blue-800 font-medium">
                  View Details →
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Detail Modal */}
      {showDetailModal && selectedBusiness && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4" onClick={() => setShowDetailModal(false)}>
          <div className="bg-white rounded-lg max-w-4xl w-full max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
            <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-4 flex items-center justify-between">
              <h2 className="text-2xl font-bold text-gray-900">Business Details</h2>
              <button
                onClick={() => setShowDetailModal(false)}
                className="text-gray-400 hover:text-gray-600"
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
            <div className="p-6">
              {/* Business Images */}
              {selectedBusiness.attachments && selectedBusiness.attachments.images && selectedBusiness.attachments.images.length > 0 && (
                <div className="mb-6">
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    {selectedBusiness.attachments.images.map((url: string, index: number) => (
                      <img
                        key={index}
                        src={url}
                        alt={`${selectedBusiness.business_name} - Image ${index + 1}`}
                        className="w-full h-64 object-cover rounded-lg"
                      />
                    ))}
                  </div>
                </div>
              )}

              {/* Business Info */}
              <div className="mb-6">
                <div className="flex items-start justify-between mb-4">
                  <div className="flex-1">
                    <h3 className="text-3xl font-bold text-gray-900 mb-2">{selectedBusiness.business_name}</h3>
                    <div className="flex flex-wrap gap-2 mb-3">
                      <span className="text-sm font-medium px-3 py-1 bg-purple-100 text-purple-800 rounded-full">
                        {selectedBusiness.category}
                      </span>
                      {selectedBusiness.is_verified && (
                        <span className="text-sm font-medium px-3 py-1 bg-green-100 text-green-800 rounded-full flex items-center">
                          <svg className="w-4 h-4 mr-1" fill="currentColor" viewBox="0 0 20 20">
                            <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                          </svg>
                          Verified
                        </span>
                      )}
                      {selectedBusiness.is_featured && (
                        <span className="text-sm font-medium px-3 py-1 bg-yellow-100 text-yellow-800 rounded-full">
                          Featured
                        </span>
                      )}
                    </div>
                  </div>
                  {selectedBusiness.rating !== undefined && selectedBusiness.rating > 0 && (
                    <div className="flex items-center">
                      <div className="flex mr-2">
                        {renderStars(selectedBusiness.rating)}
                      </div>
                      <span className="text-lg font-bold text-gray-900">{selectedBusiness.rating.toFixed(1)}</span>
                      {selectedBusiness.review_count && (
                        <span className="text-sm text-gray-500 ml-1">({selectedBusiness.review_count} reviews)</span>
                      )}
                    </div>
                  )}
                </div>

                {selectedBusiness.description && (
                  <p className="text-base text-gray-700 whitespace-pre-wrap mb-6">{selectedBusiness.description}</p>
                )}
              </div>

              {/* Contact Info Grid */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
                {selectedBusiness.contact_phone && (
                  <div className="bg-gray-50 p-4 rounded-lg">
                    <h4 className="text-sm font-medium text-gray-700 mb-2 flex items-center">
                      <PhoneIcon className="w-4 h-4 mr-2" />
                      Phone
                    </h4>
                    <a href={`tel:${selectedBusiness.contact_phone}`} className="text-base text-blue-600 hover:text-blue-800 font-medium">
                      {selectedBusiness.contact_phone}
                    </a>
                  </div>
                )}
                {selectedBusiness.contact_email && (
                  <div className="bg-gray-50 p-4 rounded-lg">
                    <h4 className="text-sm font-medium text-gray-700 mb-2 flex items-center">
                      <EmailIcon className="w-4 h-4 mr-2" />
                      Email
                    </h4>
                    <a href={`mailto:${selectedBusiness.contact_email}`} className="text-base text-blue-600 hover:text-blue-800 font-medium">
                      {selectedBusiness.contact_email}
                    </a>
                  </div>
                )}
                {selectedBusiness.website && (
                  <div className="bg-gray-50 p-4 rounded-lg">
                    <h4 className="text-sm font-medium text-gray-700 mb-2 flex items-center">
                      <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 12a9 9 0 01-9 9m9-9a9 9 0 00-9-9m9 9H3m9 9a9 9 0 01-9-9m9 9c1.657 0 3-4.03 3-9s-1.343-9-3-9m0 18c-1.657 0-3-4.03-3-9s1.343-9 3-9m-9 9a9 9 0 019-9" />
                      </svg>
                      Website
                    </h4>
                    <a
                      href={selectedBusiness.website}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-base text-blue-600 hover:text-blue-800 font-medium"
                    >
                      {selectedBusiness.website}
                    </a>
                  </div>
                )}
                {selectedBusiness.address && (
                  <div className="bg-gray-50 p-4 rounded-lg">
                    <h4 className="text-sm font-medium text-gray-700 mb-2 flex items-center">
                      <LocationIcon className="w-4 h-4 mr-2" />
                      Address
                    </h4>
                    <p className="text-base text-gray-900 font-medium">{selectedBusiness.address}</p>
                    {(selectedBusiness.block_number || selectedBusiness.flat_number) && (
                      <p className="text-sm text-gray-600 mt-1">
                        Block: {selectedBusiness.block_number || 'N/A'}, Flat: {selectedBusiness.flat_number || 'N/A'}
                      </p>
                    )}
                  </div>
                )}
              </div>

              {/* Services */}
              {selectedBusiness.services && (
                <div className="mb-6">
                  <h4 className="text-lg font-semibold text-gray-900 mb-3">Services</h4>
                  <div className="bg-gray-50 p-4 rounded-lg">
                    <p className="text-base text-gray-700 whitespace-pre-wrap">{selectedBusiness.services}</p>
                  </div>
                </div>
              )}

              {/* Business Hours */}
              {selectedBusiness.business_hours && (
                <div className="mb-6">
                  <h4 className="text-lg font-semibold text-gray-900 mb-3 flex items-center">
                    <ClockIcon className="w-5 h-5 mr-2" />
                    Business Hours
                  </h4>
                  <div className="bg-gray-50 p-4 rounded-lg">
                    {typeof selectedBusiness.business_hours === 'string' ? (
                      <p className="text-base text-gray-700">{selectedBusiness.business_hours}</p>
                    ) : (
                      <div className="space-y-2">
                        {Object.entries(selectedBusiness.business_hours).map(([day, hours]: [string, any]) => (
                          <div key={day} className="flex justify-between">
                            <span className="font-medium text-gray-900 capitalize">{day}</span>
                            <span className="text-gray-700">{hours || 'Closed'}</span>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

const GalleryTab = () => {
  const [photos, setPhotos] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedPhoto, setSelectedPhoto] = useState<any>(null);
  const [showModal, setShowModal] = useState(false);
  const [filterCategory, setFilterCategory] = useState<string>('all');
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);

  const fetchPhotos = async (pageNum: number = 1, category: string = 'all', append: boolean = false) => {
    try {
      if (!append) {
        setLoading(true);
      } else {
        setLoadingMore(true);
      }

      const params: any = {
        page: pageNum,
        limit: 20 // Load 20 photos at a time
      };

      if (category !== 'all') {
        params.category = category;
      }

      const response = await publicApi.getPhotos(params);
      if (response.success && response.data) {
        if (append) {
          setPhotos(prev => [...prev, ...response.data]);
        } else {
          setPhotos(response.data);
        }
        
        // Check if there are more pages
        const totalPages = response.pagination?.totalPages || 1;
        setHasMore(pageNum < totalPages);
      } else {
        if (!append) {
          setPhotos([]);
        }
        setHasMore(false);
      }
    } catch (error) {
      console.error('Error fetching photos:', error);
      if (!append) {
        setPhotos([]);
      }
      setHasMore(false);
    } finally {
      setLoading(false);
      setLoadingMore(false);
    }
  };

  useEffect(() => {
    setPage(1);
    setHasMore(true);
    fetchPhotos(1, filterCategory, false);
  }, [filterCategory]);

  const handleLoadMore = () => {
    if (!loadingMore && hasMore) {
      const nextPage = page + 1;
      setPage(nextPage);
      fetchPhotos(nextPage, filterCategory, true);
    }
  };

  const categories = ['all', 'general', 'events', 'maintenance', 'celebrations'];

  const handlePhotoClick = (photo: any) => {
    setSelectedPhoto(photo);
    setShowModal(true);
  };

  if (loading) {
    return (
      <div className="space-y-4 sm:space-y-6">
        <div className="text-center mb-6 sm:mb-8">
          <h2 className="text-xl sm:text-2xl md:text-3xl font-bold text-gray-900 mb-3 sm:mb-4">Photo Gallery</h2>
          <p className="text-sm sm:text-base md:text-lg text-gray-600 px-4 sm:px-0">Explore our society's moments and memories</p>
        </div>
        <div className="flex justify-center">
          <div className="animate-spin rounded-full h-6 w-6 sm:h-8 sm:w-8 border-b-2 border-blue-600"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-4 sm:space-y-6">
      <div className="text-center mb-6 sm:mb-8">
        <h2 className="text-xl sm:text-2xl md:text-3xl font-bold text-gray-900 mb-3 sm:mb-4">Photo Gallery</h2>
        <p className="text-sm sm:text-base md:text-lg text-gray-600 px-4 sm:px-0">Explore our society's moments and memories</p>
      </div>

      {/* Category Filter */}
      <div className="flex flex-wrap justify-center gap-2 sm:gap-3 mb-6 sm:mb-8">
        {categories.map((category) => (
          <button
            key={category}
            onClick={() => setFilterCategory(category)}
            disabled={loading}
            className={`px-4 py-2 rounded-full text-sm font-medium transition-colors disabled:opacity-50 ${
              filterCategory === category
                ? 'bg-blue-600 text-white'
                : 'bg-white text-gray-700 hover:bg-gray-100 border border-gray-300'
            }`}
          >
            {category.charAt(0).toUpperCase() + category.slice(1)}
          </button>
        ))}
      </div>

      {photos.length === 0 && !loading ? (
        <div className="text-center py-8 sm:py-12">
          <ImageIcon className="w-12 h-12 sm:w-16 sm:h-16 mx-auto mb-3 sm:mb-4 text-gray-400" />
          <h3 className="text-base sm:text-lg font-medium text-gray-900 mb-2">No photos available</h3>
          <p className="text-sm sm:text-base text-gray-500">Photos will be added soon.</p>
        </div>
      ) : (
        <>
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-3 sm:gap-4">
            {photos.map((photo) => (
              <div
                key={photo.id}
                className="relative group cursor-pointer"
                onClick={() => handlePhotoClick(photo)}
              >
                <div className="aspect-square rounded-lg overflow-hidden bg-gray-200 shadow-sm">
                  <img
                    src={photo.thumbnail_url || photo.image_url}
                    alt={photo.title}
                    className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-300"
                    loading="lazy"
                    onError={(e) => {
                      // Fallback to full image if thumbnail fails
                      if (e.currentTarget.src !== photo.image_url) {
                        e.currentTarget.src = photo.image_url;
                      }
                    }}
                  />
                </div>
                <div className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-40 transition-all duration-300 rounded-lg flex items-center justify-center">
                  <div className="opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                    <svg className="w-8 h-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0zM10 7v3m0 0v3m0-3h3m-3 0H7" />
                    </svg>
                  </div>
                </div>
                {photo.is_featured && (
                  <div className="absolute top-2 right-2">
                    <span className="bg-yellow-500 text-white text-xs font-medium px-2 py-1 rounded-full">
                      Featured
                    </span>
                  </div>
                )}
              </div>
            ))}
          </div>
          {hasMore && (
            <div className="text-center mt-6">
              <button
                onClick={handleLoadMore}
                disabled={loadingMore}
                className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                {loadingMore ? 'Loading...' : 'Load More'}
              </button>
            </div>
          )}
        </>
      )}

      {/* Photo Modal */}
      {showModal && selectedPhoto && (
        <div className="fixed inset-0 bg-black bg-opacity-75 z-50 flex items-center justify-center p-4" onClick={() => setShowModal(false)}>
          <div className="bg-white rounded-lg max-w-4xl w-full max-h-[90vh] overflow-auto" onClick={(e) => e.stopPropagation()}>
            <div className="relative">
              <button
                onClick={() => setShowModal(false)}
                className="absolute top-4 right-4 bg-white rounded-full p-2 hover:bg-gray-100 transition-colors z-10"
              >
                <svg className="w-6 h-6 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
              <img
                src={selectedPhoto.image_url}
                alt={selectedPhoto.title}
                className="w-full h-auto"
              />
              <div className="p-4 sm:p-6">
                <div className="flex items-center justify-between mb-2">
                  <h3 className="text-lg sm:text-xl font-semibold text-gray-900">{selectedPhoto.title}</h3>
                  <span className="bg-blue-100 text-blue-800 text-xs font-medium px-2 py-1 rounded-full">
                    {selectedPhoto.category}
                  </span>
                </div>
                {selectedPhoto.description && (
                  <p className="text-sm sm:text-base text-gray-600 mt-2">{selectedPhoto.description}</p>
                )}
                {selectedPhoto.tags && selectedPhoto.tags.length > 0 && (
                  <div className="flex flex-wrap gap-2 mt-4">
                    {selectedPhoto.tags.map((tag: string, index: number) => (
                      <span key={index} className="bg-gray-100 text-gray-600 text-xs px-2 py-1 rounded">
                        #{tag}
                      </span>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

const ManagementCommitteeTab = () => {
  const [committeeMembers, setCommitteeMembers] = useState<CommitteeMember[]>([]);
  const [loading, setLoading] = useState(true);
  const [hasFetched, setHasFetched] = useState(false);

  useEffect(() => {
    // Prevent multiple API calls in StrictMode
    if (hasFetched) return;
    
    console.log('ManagementCommitteeTab useEffect called');
    const fetchCommitteeMembers = async () => {
      try {
        console.log('Fetching committee members...');
        setHasFetched(true);
        // Fetch real committee members from API
        const response = await fetch('/api/v1/committee');
        if (response.ok) {
          const data = await response.json();
          const members = data.data || [];
          // Sort members by hierarchy as fallback
          const sortedMembers = members.sort((a: CommitteeMember, b: CommitteeMember) => {
            const getPositionOrder = (position: string) => {
              const pos = position.toLowerCase();
              if (pos.includes('president')) return 1;
              if (pos.includes('vice president')) return 2;
              if (pos.includes('general secretary') || pos.includes('gen.secretary')) return 3;
              if (pos.includes('treasurer')) return 4;
              if (pos.includes('member') || pos.includes('m.c. members')) return 5;
              return 6;
            };
            
            const orderA = getPositionOrder(a.position);
            const orderB = getPositionOrder(b.position);
            
            if (orderA !== orderB) {
              return orderA - orderB;
            }
            
            // If same position, sort by name
            return a.name.localeCompare(b.name);
          });
          setCommitteeMembers(sortedMembers);
        } else {
          // Fallback to empty array if API fails
          setCommitteeMembers([]);
        }
      } catch (error) {
        console.error('Error fetching committee members:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchCommitteeMembers();
  }, [hasFetched]);

  if (loading) {
    return (
      <div className="space-y-4 sm:space-y-6">
        <div className="text-center mb-6 sm:mb-8">
          <h2 className="text-xl sm:text-2xl md:text-3xl font-bold text-gray-900 mb-3 sm:mb-4">Management Committee</h2>
          <p className="text-sm sm:text-base md:text-lg text-gray-600 px-4 sm:px-0">Meet our dedicated team working for the community</p>
        </div>
        <div className="flex justify-center">
          <div className="animate-spin rounded-full h-6 w-6 sm:h-8 sm:w-8 border-b-2 border-blue-600"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-4 sm:space-y-6">
      <div className="text-center mb-6 sm:mb-8">
        <h2 className="text-xl sm:text-2xl md:text-3xl font-bold text-gray-900 mb-3 sm:mb-4">Management Committee</h2>
        <p className="text-sm sm:text-base md:text-lg text-gray-600 px-4 sm:px-0">Meet our dedicated team working for the community</p>
      </div>

      {committeeMembers.length === 0 ? (
        <div className="text-center py-8 sm:py-12">
          <div className="w-12 h-12 sm:w-16 sm:h-16 mx-auto mb-3 sm:mb-4 bg-gray-100 rounded-full flex items-center justify-center">
            <svg className="w-6 h-6 sm:w-8 sm:h-8 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197m13.5-9a2.5 2.5 0 11-5 0 2.5 2.5 0 015 0z" />
            </svg>
          </div>
          <h3 className="text-base sm:text-lg font-medium text-gray-900 mb-2">No committee information</h3>
          <p className="text-sm sm:text-base text-gray-500">Committee details will be updated soon.</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6">
          {committeeMembers.map((member) => (
            <div key={member.id} className="bg-white rounded-xl p-4 sm:p-6 shadow-sm border border-gray-200 hover:shadow-md transition-shadow">
              <div className="text-center mb-3 sm:mb-4">
                <div className="w-16 h-16 sm:w-20 sm:h-20 mx-auto mb-3 sm:mb-4 bg-gray-200 rounded-full flex items-center justify-center">
                  {member.profile_picture && (
                    <img
                      src={getProfileImageUrl(member.profile_picture)}
                      alt={member.name}
                      className="w-full h-full rounded-full object-cover"
                      onError={(e) => {
                        e.currentTarget.style.display = 'none';
                        const fallback = e.currentTarget.nextElementSibling as HTMLElement | null;
                        if (fallback) {
                          fallback.style.display = 'flex';
                        }
                      }}
                    />
                  )}
                  <div
                    className="w-full h-full rounded-full items-center justify-center"
                    style={{ display: member.profile_picture ? 'none' : 'flex' }}
                  >
                    <svg className="w-8 h-8 sm:w-10 sm:h-10 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                    </svg>
                  </div>
                </div>
                <h3 className="text-lg sm:text-xl font-semibold text-gray-900 mb-1">{member.name}</h3>
                <div className="inline-block bg-blue-100 text-blue-800 text-xs sm:text-sm font-medium px-2 sm:px-3 py-1 rounded-full mb-2 sm:mb-3">
                  {member.position}
                </div>
              </div>
              
              <div className="space-y-2 sm:space-y-3">
                {(member.bio || member.description) && (
                  <p className="text-xs sm:text-sm text-gray-600 text-center">{member.bio || member.description}</p>
                )}
                
                <div className="space-y-1 sm:space-y-2">
                  {member.phone && (
                    <div className="flex items-center text-xs sm:text-sm text-gray-500">
                      <PhoneIcon className="w-3 h-3 sm:w-4 sm:h-4 mr-2 text-gray-400 flex-shrink-0" />
                      <span className="truncate">{member.phone}</span>
                    </div>
                  )}
                  {member.email && (
                    <div className="flex items-center text-xs sm:text-sm text-gray-500">
                      <EmailIcon className="w-3 h-3 sm:w-4 sm:h-4 mr-2 text-gray-400 flex-shrink-0" />
                      <span className="truncate">{member.email}</span>
                    </div>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export const HomePage: React.FC = () => {
  const [activeTab, setActiveTab] = useState('home')
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false)

  const tabs = [
    { id: 'home', label: 'Home', component: HomeTab },
    { id: 'events', label: 'Events', component: EventsTab },
    { id: 'notifications', label: 'Notifications', component: NotificationsTab },
    { id: 'press-release', label: 'Press Release', component: PressReleaseTab },
    { id: 'business', label: 'Business', component: BusinessTab },
    { id: 'gallery', label: 'Gallery', component: GalleryTab },
    { id: 'management-committee', label: 'Management Committee', component: ManagementCommitteeTab },
  ]

  const ActiveComponent = tabs.find(tab => tab.id === activeTab)?.component || HomeTab

  return (
    <div className="bg-gray-50 min-h-screen">
      {/* Tab Navigation */}
      <div className="bg-white border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Mobile Menu Button */}
          <div className="flex items-center justify-between lg:hidden py-4">
            <h2 className="text-lg font-semibold text-gray-900">Menu</h2>
            <button
              onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
              className="p-2 rounded-md text-gray-400 hover:text-gray-500 hover:bg-gray-100"
            >
              <svg className="h-6 w-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
              </svg>
            </button>
          </div>

          {/* Desktop Navigation */}
          <nav className="hidden lg:flex space-x-8">
            {tabs.map((tab) => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`py-4 px-1 border-b-2 font-medium text-sm whitespace-nowrap ${
                  activeTab === tab.id
                    ? 'border-blue-500 text-blue-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                {tab.label}
              </button>
            ))}
          </nav>

          {/* Mobile Navigation */}
          {isMobileMenuOpen && (
            <nav className="lg:hidden border-t border-gray-200 py-4">
              <div className="space-y-1">
                {tabs.map((tab) => (
                  <button
                    key={tab.id}
                    onClick={() => {
                      setActiveTab(tab.id)
                      setIsMobileMenuOpen(false)
                    }}
                    className={`w-full text-left px-3 py-2 rounded-md text-sm font-medium ${
                      activeTab === tab.id
                        ? 'bg-blue-100 text-blue-700'
                        : 'text-gray-600 hover:bg-gray-50 hover:text-gray-900'
                    }`}
                  >
                    {tab.label}
                  </button>
                ))}
              </div>
            </nav>
          )}
        </div>
      </div>

      {/* Tab Content */}
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4 sm:py-6 lg:py-8">
        <ActiveComponent />
      </main>
    </div>
  )
}

export default HomePage
